import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
    Card,
    CardContent,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
    Search, 
    Filter, 
    CheckCircle2, 
    XCircle, 
    Clock, 
    AlertTriangle, 
    User, 
    CreditCard, 
    Eye,
    MessageSquare,
    Loader2,
    ShieldAlert
} from 'lucide-react';
import { transactionsApi, TransactionReview, ReviewStatus } from '../../api/transactions.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import { 
    Dialog, 
    DialogContent, 
    DialogDescription, 
    DialogFooter, 
    DialogHeader, 
    DialogTitle 
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const formatHTG = (amount: string | number) => {
    return new Intl.NumberFormat('fr-HT', { style: 'currency', currency: 'HTG' }).format(Number(amount));
};

export default function TransactionReviewsPage() {
    const { toast } = useToast();
    const [reviews, setReviews] = useState<TransactionReview[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<ReviewStatus>('pending');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal state
    const [selectedReview, setSelectedReview] = useState<TransactionReview | null>(null);
    const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
    const [adminNotes, setAdminNotes] = useState('');
    const [processingAction, setProcessingAction] = useState(false);

    const loadReviews = useCallback(async () => {
        try {
            setLoading(true);
            const response = await transactionsApi.listReviews({ status: statusFilter });
            setReviews(response.results);
        } catch (err) {
            console.error('Error loading reviews:', err);
            toast({ title: 'Erè', description: 'Pa kapab chaje tranzaksyon yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, toast]);

    useEffect(() => {
        loadReviews();
    }, [statusFilter, loadReviews]);

    const handleAction = async () => {
        if (!selectedReview || !actionType) return;

        try {
            setProcessingAction(true);
            await transactionsApi.actionReview(selectedReview.id, actionType, adminNotes);
            
            toast({ 
                title: 'Siksè', 
                description: `Tranzaksyon ${actionType === 'approve' ? 'apwouve' : 'rejete'} avèk siksè`, 
                type: 'success' 
            });
            
            // Refresh list and close modal
            setSelectedReview(null);
            setActionType(null);
            setAdminNotes('');
            loadReviews();
        } catch (err: any) {
            console.error('Error processing action:', err);
            toast({ 
                title: 'Erè', 
                description: err.response?.data?.error || 'Yon erè rive pandan tretman an', 
                type: 'error' 
            });
        } finally {
            setProcessingAction(false);
        }
    };

    const getRestrictionIcon = (type: string) => {
        switch (type) {
            case 'time_restriction': return <Clock className="h-4 w-4 text-blue-500" />;
            case 'amount_bs': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
            case 'amount_sol': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
            case 'admin_limit': return <ShieldAlert className="h-4 w-4 text-red-600" />;
            default: return <Clock className="h-4 w-4" />;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending': return <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">En Attente</Badge>;
            case 'approved': return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Approuvé</Badge>;
            case 'rejected': return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">Rejeté</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
                    {/* Breadcrumb */}
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink>Administrasyon</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>Validasyon Tranzaksyon</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Validasyon Tranzaksyon</h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Tranzaksyon ki bloke pou montant elevé oswa orè restrent.
                            </p>
                        </div>
                    </div>

                    {/* Stats & Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border-none shadow-sm bg-primary-600 text-white">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-primary-100 text-xs font-semibold uppercase">Pending</p>
                                        <p className="text-3xl font-bold mt-1">{reviews.filter(r => r.status === 'pending').length}</p>
                                    </div>
                                    <div className="p-3 bg-white/20 rounded-xl">
                                        <Clock className="h-6 w-6" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Status Filter Tabs */}
                        <div className="md:col-span-2 flex items-center gap-2 bg-white p-1 rounded-xl border border-slate-200 h-fit self-end">
                            <button 
                                onClick={() => setStatusFilter('pending')}
                                className={cn(
                                    "flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                                    statusFilter === 'pending' ? "bg-amber-500 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"
                                )}
                            >
                                En Attente
                            </button>
                            <button 
                                onClick={() => setStatusFilter('approved')}
                                className={cn(
                                    "flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                                    statusFilter === 'approved' ? "bg-green-600 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"
                                )}
                            >
                                Approuvé
                            </button>
                            <button 
                                onClick={() => setStatusFilter('rejected')}
                                className={cn(
                                    "flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                                    statusFilter === 'rejected' ? "bg-red-600 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"
                                )}
                            >
                                Rejeté
                            </button>
                        </div>
                    </div>

                    {/* Search & Action Bar */}
                    <div className="flex items-center gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                            <Input 
                                placeholder="Recherche par agent ou compte..." 
                                className="pl-10 h-10 border-slate-200 focus:ring-primary-500 rounded-xl"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <Button variant="outline" className="gap-2 h-10 px-4 rounded-xl border-slate-200">
                            <Filter className="h-4 w-4" />
                            <span>Filtrer</span>
                        </Button>
                    </div>

                    {/* Table Card */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-200">
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Motif & Date</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Agent</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Client / Compte</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Montant</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Statut</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-12 text-center">
                                                <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                                <p className="text-slate-500 mt-2">Chajman tranzaksyon yo...</p>
                                            </td>
                                        </tr>
                                    ) : reviews.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-12 text-center">
                                                <div className="bg-slate-50 rounded-full h-12 w-12 flex items-center justify-center mx-auto text-slate-300">
                                                    <CreditCard className="h-6 w-6" />
                                                </div>
                                                <p className="text-slate-500 mt-2">Okenn tranzaksyon pa jwenn.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        reviews
                                            .filter(r => 
                                                r.agent_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                                r.compte_code.includes(searchTerm) ||
                                                (r.client_name && r.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
                                            )
                                            .map((review) => (
                                            <tr key={review.id} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                                                            {getRestrictionIcon(review.restriction_type)}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-slate-900 leading-none">
                                                                {review.restriction_type_display}
                                                            </p>
                                                            <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                                                                <Clock className="h-3 w-3" />
                                                                {new Date(review.created_at).toLocaleString('fr-FR')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-7 w-7 rounded-full bg-primary-100 flex items-center justify-center">
                                                            <User className="h-3.5 w-3.5 text-primary-600" />
                                                        </div>
                                                        <span className="text-sm font-medium text-slate-700">{review.agent_name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-sm font-bold text-slate-900">{review.client_name || 'N/A'}</span>
                                                        <span className="text-xs text-slate-400 font-mono">{review.compte_code}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-bold text-slate-900">
                                                    {formatHTG(review.montant)}
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    {getStatusBadge(review.status)}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    {review.status === 'pending' ? (
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button 
                                                                size="sm" 
                                                                className="bg-green-600 hover:bg-green-700 h-8 gap-1.5 shadow-sm"
                                                                onClick={() => {
                                                                    setSelectedReview(review);
                                                                    setActionType('approve');
                                                                }}
                                                            >
                                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                                Apwouve
                                                            </Button>
                                                            <Button 
                                                                size="sm" 
                                                                variant="outline"
                                                                className="text-red-600 border-red-100 hover:bg-red-50 hover:text-red-700 h-8 gap-1.5"
                                                                onClick={() => {
                                                                    setSelectedReview(review);
                                                                    setActionType('reject');
                                                                }}
                                                            >
                                                                <XCircle className="h-3.5 w-3.5" />
                                                                Rejete
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                            <Eye className="h-4 w-4 text-slate-400" />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Action Dialog */}
            <Dialog 
                open={!!selectedReview && !!actionType} 
                onOpenChange={(open) => {
                    if(!open) {
                        setSelectedReview(null);
                        setActionType(null);
                        setAdminNotes('');
                    }
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {actionType === 'approve' ? (
                                <>
                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                    <span>Apwouve Tranzaksyon</span>
                                </>
                            ) : (
                                <>
                                    <XCircle className="h-5 w-5 text-red-600" />
                                    <span>Rejete Tranzaksyon</span>
                                </>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            Èske w asire w vle {actionType === 'approve' ? 'validé' : 'rejete'} tranzaksyon de <span className="font-bold">{formatHTG(selectedReview?.montant || 0)}</span> fè pa <span className="font-bold">{selectedReview?.agent_name}</span>?
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-slate-400" />
                                Nòt Administrativ (Opsyonèl)
                            </label>
                            <Textarea 
                                placeholder="Ajoute yon eksplikasyon si sa nesesè..."
                                className="resize-none min-h-[100px] border-slate-200 focus:ring-primary-500"
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setSelectedReview(null);
                                setActionType(null);
                                setAdminNotes('');
                            }}
                            className="border-slate-200"
                        >
                            Anile
                        </Button>
                        <Button 
                            className={cn(
                                "gap-2 shadow-md",
                                actionType === 'approve' ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                            )}
                            onClick={handleAction}
                            disabled={processingAction}
                        >
                            {processingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                                actionType === 'approve' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />
                            )}
                            {actionType === 'approve' ? 'Konfime Apwobasyon' : 'Konfime Rejè'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
